import { topicRanks } from "@/mocks/cg51";
import { Reveal, SectionHeading } from "./ui";

const trendIcon: Record<string, string> = { up: "ri-arrow-up-line text-primary-600", down: "ri-arrow-down-line text-secondary-500", flat: "ri-arrow-right-line text-foreground-400" };
const rankStyle = (r: number) =>
  r === 1 ? "bg-primary-500 text-background-50" : r === 2 ? "bg-accent-500 text-foreground-950" : r === 3 ? "bg-secondary-200 text-secondary-900" : "bg-background-200 text-foreground-600";

export default function Topics() {
  return (
    <section id="topics" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:gap-12">
          {/* 左：话题大全榜 */}
          <div>
            <Reveal>
              <SectionHeading
                eyebrow="Topic Library"
                title="话题大全 · 此刻最该点开什么"
                desc="话题大全综合回复条数、更新时间与讨论增速给出热度，帮你一眼看清现在最热的方向，再顺着分区走进去细看。"
              />
            </Reveal>
            <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {topicRanks.map((t, idx) => (
                <Reveal key={t.rank} delay={idx * 40}>
                  <a
                    href="#topics"
                    className={`group flex items-center gap-4 px-4 py-4 transition-colors hover:bg-background-100 md:gap-6 md:px-6 ${
                      idx !== topicRanks.length - 1 ? "border-b border-background-200" : ""
                    }`}
                  >
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${rankStyle(t.rank)}`}>
                      {t.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-medium text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">{t.title}</h3>
                        <i className={`${trendIcon[t.trend]} text-sm`} />
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="whitespace-nowrap text-xs text-foreground-500">{t.zone}</span>
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                          <div className="h-full rounded-full bg-gradient-to-r from-accent-400 to-primary-500" style={{ width: `${t.heat}%` }} />
                        </div>
                        <span className="w-12 shrink-0 whitespace-nowrap text-right text-xs font-semibold text-primary-700">{t.heat} 热度</span>
                      </div>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* 右：侧栏 */}
          <aside className="flex flex-col gap-5 lg:pt-24">
            <Reveal>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-timer-flash-line text-lg" />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">分钟级热度</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  话题大全的榜单不是一天一更，而是跟着讨论增速动态排序。爆冷、官宣这类突发事，热度条会在几分钟内爬上榜首。
                </p>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">本周上升最快分区</h3>
                <div className="mt-4 space-y-3">
                  {[
                    { name: "电竞区", delta: "+32%", icon: "ri-gamepad-line" },
                    { name: "影视区", delta: "+28%", icon: "ri-film-line" },
                    { name: "音乐区", delta: "+21%", icon: "ri-music-2-line" },
                  ].map((z) => (
                    <div key={z.name} className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5">
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground-900">
                        <i className={`${z.icon} text-primary-600`} /> {z.name}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent-700">
                        <i className="ri-arrow-up-line" /> {z.delta}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="overflow-hidden rounded-lg bg-foreground-950 p-6">
                <h3 className="font-heading text-xl font-bold text-background-50">怕错过今天的热点？</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-50/70">把 cg51 收进收藏，每天回来扫一眼话题大全与热点专楼，热门讨论一页掌握。</p>
                <button type="button" className="mt-4 w-full whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400">
                  收藏本站
                </button>
              </div>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}